import { useEffect, useState } from "react";
import { Priority } from "@a46-rss/shared";
import { supabase } from "../lib/supabase";
import { ArticleListItem } from "../hooks/useArticles";

interface ArticleModalProps {
  article: ArticleListItem;
  onClose: () => void;
}

const priorityLabels: Record<Priority, string> = {
  1: "最高",
  2: "高",
  3: "中",
  4: "低",
  5: "最低",
};

// 本文を段落に分割して整形する関数
function formatContent(content: string): React.ReactNode[] {
  // 改行で分割
  const lines = content.split(/\n+/);
  const elements: { type: string; content: string }[] = [];
  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(" ").trim();
      if (text) {
        elements.push({ type: "paragraph", content: text });
      }
      currentParagraph = [];
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushParagraph();
      continue;
    }

    // 見出しっぽい行
    if (
      (trimmedLine.length < 50 &&
        !trimmedLine.endsWith("。") &&
        !trimmedLine.endsWith("、")) ||
      /^[●■◆▼▲【「]/.test(trimmedLine)
    ) {
      flushParagraph();
      elements.push({ type: "heading", content: trimmedLine });
      continue;
    }

    // 箇条書きっぽい行
    if (/^[・\-\*•]/.test(trimmedLine) || /^[0-9]+[.．)）]/.test(trimmedLine)) {
      flushParagraph();
      const cleanedContent = trimmedLine
        .replace(/^[・\-\*•]\s*/, "")
        .replace(/^[0-9]+[.．)）]\s*/, "");
      elements.push({ type: "listitem", content: cleanedContent });
      continue;
    }

    currentParagraph.push(trimmedLine);
  }

  flushParagraph();

  // React要素に変換
  const reactElements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let paragraphIndex = 0;

  const flushListItems = () => {
    if (listItems.length > 0) {
      reactElements.push(
        <ul
          key={`ul-${reactElements.length}`}
          className="my-4 ml-2 space-y-2 border-l-2 border-blue-200 pl-4 bg-blue-50 py-3 rounded-r-lg"
        >
          {listItems}
        </ul>,
      );
      listItems = [];
    }
  };

  for (const element of elements) {
    if (element.type === "listitem") {
      listItems.push(
        <li
          key={`li-${listItems.length}`}
          className="text-gray-700 leading-relaxed text-sm"
        >
          <span className="text-blue-400 mr-2">●</span>
          {element.content}
        </li>,
      );
    } else {
      flushListItems();

      if (element.type === "heading") {
        reactElements.push(
          <div
            key={`h-${reactElements.length}`}
            className="mt-6 mb-3 bg-gray-100 px-4 py-2 rounded-lg border-l-4 border-gray-400"
          >
            <h4 className="font-bold text-gray-900 text-base">
              {element.content}
            </h4>
          </div>,
        );
      } else if (element.type === "paragraph") {
        const isEven = paragraphIndex % 2 === 0;
        reactElements.push(
          <div
            key={`p-${reactElements.length}`}
            className={`p-4 rounded-lg mb-2 border-l-4 ${
              isEven
                ? "bg-white border-blue-300"
                : "bg-gray-50 border-green-300"
            }`}
          >
            <p className="text-gray-700 leading-loose text-sm">
              {element.content}
            </p>
          </div>,
        );
        paragraphIndex++;
      }
    }
  }

  flushListItems();

  return reactElements;
}

export default function ArticleModal({ article, onClose }: ArticleModalProps) {
  const [fullContent, setFullContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      const { data } = await supabase
        .from("articles")
        .select("content")
        .eq("id", article.id)
        .single();

      setFullContent(data?.content || null);
      setLoading(false);
    }

    fetchContent();
  }, [article.id]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const priority = article.priority as Priority;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* ヘッダー */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between z-10">
            <div className="flex-1 pr-4">
              <h2 className="text-xl font-bold text-gray-900 leading-tight">
                {article.title}
              </h2>
              <div className="mt-2 flex items-center flex-wrap gap-2 text-sm text-gray-500">
                <span className="font-medium text-gray-700">
                  {article.source_name}
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  {article.published_at
                    ? new Date(article.published_at).toLocaleDateString(
                        "ja-JP",
                        { year: "numeric", month: "long", day: "numeric" },
                      )
                    : "日付不明"}
                </span>
                <span className="text-gray-300">|</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium priority-badge priority-${priority}`}
                >
                  P{priority} {priorityLabels[priority]}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* コンテンツ */}
          <div className="px-6 py-5 overflow-y-auto max-h-[65vh]">
            {/* サマリー */}
            {article.summary && (
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center">
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  サマリー
                </h3>
                <p className="text-sm text-yellow-900 leading-relaxed">
                  {article.summary}
                </p>
              </div>
            )}

            {/* 本文 */}
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center border-b pb-2">
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                本文
              </h3>
              {loading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-11/12"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              ) : fullContent ? (
                <div className="space-y-1">
                  {formatContent(fullContent.slice(0, 5000))}
                  {fullContent.length > 5000 && (
                    <p className="mt-4 text-sm text-gray-500 italic bg-gray-100 p-3 rounded">
                      ※
                      本文が長いため一部を省略しています。続きは元記事をご覧ください。
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  本文を取得できませんでした。元記事をご確認ください。
                </p>
              )}
            </div>
          </div>

          {/* フッター */}
          <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-between items-center">
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              元記事を開く
              <svg
                className="ml-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
